// Pure maths and validation for the entry form (architecture sprint-03 §9.3). State is held in
// seconds; every display string is derived here. Mirrors the server rules (entries.types.ts).
import { diffDays, durationToLocalEnd, isValidLocalDate, localSpanEnd, splitLocalSpan, type IanaZone, type LocalDate } from '@shared/calendar';
import { MAX_DESCRIPTION_LENGTH, MAX_ENTRY_DATE, MAX_ENTRY_SECONDS, MIN_ENTRY_DATE } from '@shared/entry-rules';

const SECONDS_PER_HOUR = 3600;
const SECONDS_PER_MINUTE = 60;
const SECONDS_PER_DAY = 86_400;
const DURATION_PATTERN = /^(\d{1,2}):([0-5]\d)$/;
const TIME_PATTERN = /^(\d{2}):(\d{2})(?::(\d{2}))?$/;

export const DURATION_RANGE_ERROR = 'Duration must be more than 0 and no more than 24:00';
export const DURATION_FORMAT_ERROR = 'Enter the duration as H:MM';
export const DATE_ERROR = 'Enter a valid date';
export const START_ERROR = 'Enter a start time';
export const DESCRIPTION_ERROR = 'Use at most 500 characters';

export type ParsedDuration = { ok: true; seconds: number } | { ok: false; error: string };

/** 'H:MM' or 'HH:MM' → seconds; 0:00 and anything over 24:00 are errors. */
export function parseDuration(text: string): ParsedDuration {
  const match = DURATION_PATTERN.exec(text.trim());
  if (!match) return { ok: false, error: DURATION_FORMAT_ERROR };
  const seconds = Number(match[1]) * SECONDS_PER_HOUR + Number(match[2]) * SECONDS_PER_MINUTE;
  if (seconds <= 0 || seconds > MAX_ENTRY_SECONDS) return { ok: false, error: DURATION_RANGE_ERROR };
  return { ok: true, seconds };
}

/** Seconds → 'H:MM', rounded to the nearest minute. */
export function formatDurationInput(seconds: number): string {
  const totalMinutes = Math.round(seconds / SECONDS_PER_MINUTE);
  return `${Math.floor(totalMinutes / 60)}:${String(totalMinutes % 60).padStart(2, '0')}`;
}

/** Seconds since midnight → 'HH:MM' for <input type="time"> (seconds are truncated for display). */
export function secondsToTimeInput(second: number): string {
  const safe = ((second % SECONDS_PER_DAY) + SECONDS_PER_DAY) % SECONDS_PER_DAY;
  const hours = Math.floor(safe / SECONDS_PER_HOUR);
  const minutes = Math.floor((safe % SECONDS_PER_HOUR) / SECONDS_PER_MINUTE);
  return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
}

/** 'HH:MM' (or 'HH:MM:SS') → seconds since midnight; '' or malformed → null. */
export function timeInputToSeconds(value: string): number | null {
  const match = TIME_PATTERN.exec(value);
  if (!match) return null;
  const hours = Number(match[1]);
  const minutes = Number(match[2]);
  const seconds = Number(match[3] ?? '0');
  if (hours > 23 || minutes > 59 || seconds > 59) return null;
  return hours * SECONDS_PER_HOUR + minutes * SECONDS_PER_MINUTE + seconds;
}

export function isValidEntryDate(date: string): boolean {
  return isValidLocalDate(date) && date >= MIN_ENTRY_DATE && date <= MAX_ENTRY_DATE;
}

export interface DerivedEnd {
  second: number;
  /** Local days after `date` on which the span ends (0 = same day). */
  dayOffset: number;
}

/** The DST-aware End shown in the form; the same function the server split uses. */
export function deriveEnd(date: LocalDate, startSecond: number, durationSeconds: number, tz: IanaZone): DerivedEnd {
  const end = localSpanEnd(date, startSecond, durationSeconds, tz);
  return { second: end.second, dayOffset: diffDays(end.date, date) };
}

/** '' on the same day, '+1 day', '+2 days', … */
export function endDayMarker(dayOffset: number): string {
  if (dayOffset <= 0) return '';
  return dayOffset === 1 ? '+1 day' : `+${dayOffset} days`;
}

/** Editing End: real seconds from Start to that clock time; End at or before Start reads as overnight. */
export function durationFromEnd(date: LocalDate, startSecond: number, endSecond: number, tz: IanaZone): number {
  return durationToLocalEnd(date, startSecond, endSecond, tz);
}

/** The dates the entry will be saved on (one per local day); length > 1 means it crosses midnight. */
export function savedDates(date: LocalDate, startSecond: number, durationSeconds: number, tz: IanaZone): LocalDate[] {
  return splitLocalSpan(date, startSecond, durationSeconds, tz).map((s) => s.date);
}

export interface EntryFormValues {
  date: string;
  startSecond: number | null;
  durationSeconds: number | null;
  description: string;
}

export interface EntryFormErrors {
  date?: string;
  startSecond?: string;
  durationSeconds?: string;
  description?: string;
}

export function validateEntryForm(values: EntryFormValues): EntryFormErrors {
  const errors: EntryFormErrors = {};
  if (!isValidEntryDate(values.date)) errors.date = DATE_ERROR;
  if (values.startSecond === null) errors.startSecond = START_ERROR;
  if (values.durationSeconds === null || values.durationSeconds <= 0 || values.durationSeconds > MAX_ENTRY_SECONDS) {
    errors.durationSeconds = DURATION_RANGE_ERROR;
  }
  if (values.description.trim().length > MAX_DESCRIPTION_LENGTH) errors.description = DESCRIPTION_ERROR;
  return errors;
}

export function hasErrors(errors: EntryFormErrors): boolean {
  return Object.keys(errors).length > 0;
}
